const user = (window.app && window.app.user) || {}
const allowedActivities = user.allowed_activities || []

export const menu = {
  grow: {
    title: "Grow",
    links: [
      {
        href: "/subscribers",
        label: "Subscribers",
        content: "Browse all your subscribers and customers.",
        icon: "three-people",
        iconColor: "text-black"
      },
      {
        label: "Creator Profile",
        href: "/creator_profile",
        content:
          "Your hub on the internet. A one-stop-shop for your subscribers to find all your content & products.",
        icon: "creator-profile",
        iconColor: "text-black"
      },
      {
        label: "Landing Pages & Forms",
        href: "/forms",
        content:
          "Gather subscribers with a hosted landing page or a form embedded on your website.",
        icon: "landing-page",
        iconColor: "text-black"
      },
      {
        label: "Recommendations",
        href: "/creator-network",
        content:
          "Join the Creator Network to cross-promote your work with other creators and grow your email lists together.",
        icon: "creator-network",
        iconColor: "text-black"
      }
    ]
  },
  send: {
    title: "Send",
    links: [
      {
        href: "/campaigns",
        label: "Broadcasts",
        content:
          "Send an email to some, or all, of your audience, and to your public feed.",
        icon: "megaphone",
        iconColor: "text-black"
      },
      {
        href: "/account/email_layout_templates",
        label: "Email Templates",
        content:
          "Create email templates to use as a starting point in your emails.",
        icon: "template",
        iconColor: "text-black"
      },
      {
        href: "/sequences",
        label: "Sequences",
        content: "Create multiple emails that are sent in order.",
        icon: "emails",
        isCreatorFeat: true,
        canUseFeat: allowedActivities.includes("sequences"),
        iconColor: "text-black"
      },
      {
        href: "/account/snippets",
        label: "Snippets",
        content: "Supercharge your emails with reusable blocks of content.",
        icon: "cube",
        iconColor: "text-black"
      }
    ]
  },
  automate: {
    title: "Automate",
    links: [
      {
        href: "/automations",
        label: "Visual Automations",
        content: "Create powerful, automated funnels for your audience.",
        icon: "bolt",
        isCreatorFeat: true,
        canUseFeat: allowedActivities.includes("va"),
        iconColor: "text-black"
      },
      {
        href: "/apps",
        label: "App Store",
        content:
          "Power up your account by connecting with your favorite apps and integrations.",
        icon: "plug",
        iconColor: "text-black",
        canUseFeat: allowedActivities.includes("apps"),
        isCreatorFeat: true
      },
      {
        href: "/rules",
        label: "Rules",
        content: '"If this, then that" automation rules.',
        icon: "rules",
        iconColor: "text-black",
        canUseFeat: allowedActivities.includes("rules"),
        isCreatorFeat: true
      },
      {
        href: "/feeds",
        label: "RSS",
        content:
          "Automagically generate emails or digests when RSS feeds update.",
        icon: "rss",
        canUseFeat: allowedActivities.includes("rss"),
        isCreatorFeat: true,
        iconColor: "text-black"
      }
    ]
  },
  earn: {
    title: "Earn",
    links: [
      {
        href: "/products",
        label: "Products",
        content:
          "Sell digital products and content subscriptions directly to your audience.",
        icon: "bag",
        iconColor: "text-black"
      },
      {
        href: "/paid_recommendations",
        label: "Paid Recommendations",
        content: "Get paid to recommend other creators in the Creator Network.",
        icon: "rec-paid",
        iconColor: "text-black"
      },
      {
        href: "/ads",
        label: "Ads",
        content: "Monetize your emails with programmatic advertising. ",
        icon: "ad",
        iconColor: "text-black",
        showBetaBadge: true
      },
      {
        href: "/tipjars",
        label: "Tip Jars",
        content: "Give your audience a way to send you a tip for your content.",
        icon: "tip-jar",
        iconColor: "text-black"
      },
      {
        href: "/account_settings/commerce/#payouts",
        label: "Payouts",
        content: "See a breakdown of all payouts you’ve received.",
        icon: "wallet",
        iconColor: "text-black"
      }
    ]
  },
  learn: {
    title: "Learn",
    links: [
      {
        href: "https://help.convertkit.com/",
        label: "Documentation",
        content: "Read the documentation for helpful articles & tutorials.",
        icon: "book",
        iconColor: "text-black",
        newTab: true
      },
      {
        href: "/community",
        label: "Community",
        content: "Got a burning question? Ask the community for advice!",
        icon: "chat-conversation",
        iconColor: "text-black",
        newTab: true
      },
      {
        href: "https://learn.convertkit.com/courses",
        label: "Creator University",
        content:
          "In-depth courses to help you earn a living as a creator with Kit.",
        icon: "education",
        iconColor: "text-black",
        newTab: true
      }
    ]
  }
}

export const settings = (isAdmin, { accountId = 0, accounts = [] }) => {
  const accountUsers = Array.isArray(accounts) ? accounts : []
  const admin = !isAdmin
    ? {}
    : {
        admin: {
          icon: "profile",
          label: "Admin",
          color: "text-black",
          href: "/admin"
        },
        viewInAdmin: {
          icon: "view",
          label: "View account in admin",
          color: "text-black",
          href: `/admin/accounts/${accountId}`
        }
      }

  const getAccessLabel = accountData => {
    if (accountData.unpaid) {
      return "[no access, unpaid] "
    } else if (accountData.trial_expired) {
      return "[no access, trial expired] "
    }

    return ""
  }

  const switchUsers =
    accountUsers.length < 1
      ? {}
      : {
          accounts: {
            label: "Switch Creator Accounts",
            method: "post",
            color: "text-gray-900",
            data: accountUsers
              .map(account => ({
                ...account,
                label: `${getAccessLabel(account)}${account.name ||
                  account.email}`,
                href: `/account_users/${account.id}/switch`
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
          }
        }

  const settings = {
    settings: {
      icon: "settings-cog",
      label: "Settings",
      color: "text-black",
      href: "/account_settings/account_info"
    }
  }

  return {
    ...admin,
    ...settings,
    help: {
      icon: "support",
      label: "Help",
      color: "text-black",
      href: "mailto:help@kit.com",
      newTab: true
    },
    ...switchUsers,
    logout: {
      icon: "log-out",
      label: "Log out",
      color: "text-gray-800",
      href: "/users/logout",
      method: "delete"
    }
  }
}
